import { DragEvent, useCallback, useRef, useState } from "react";

interface FileDropzone {
	/** True while a drag is over the zone, for the caller's highlight class. */
	isDraggingOver: boolean;
	/** Spread onto the element that should accept drops. */
	dropzoneProps: {
		onDragEnter: (event: DragEvent<HTMLElement>) => void;
		onDragOver: (event: DragEvent<HTMLElement>) => void;
		onDragLeave: (event: DragEvent<HTMLElement>) => void;
		onDrop: (event: DragEvent<HTMLElement>) => void;
	};
}

/**
 * Drag-and-drop for an existing file picker: hands the dropped FileList to the same
 * callback the `<input type="file">` onChange already uses.
 *
 * Dragging over a child element fires dragleave on the parent, so enter/leave are counted
 * rather than toggled — otherwise the highlight flickers as the pointer crosses the icon
 * and labels inside the zone.
 */
const useFileDropzone = (onFiles: (files: FileList | null) => void, disabled = false): FileDropzone => {
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const dragDepth = useRef(0);

	const reset = useCallback(() => {
		dragDepth.current = 0;
		setIsDraggingOver(false);
	}, []);

	const onDragEnter = useCallback(
		(event: DragEvent<HTMLElement>) => {
			event.preventDefault();
			if (disabled) return;
			dragDepth.current += 1;
			setIsDraggingOver(true);
		},
		[disabled]
	);

	// Without preventDefault here the browser opens the file instead of dropping it.
	const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);

	const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
		event.preventDefault();
		dragDepth.current = Math.max(0, dragDepth.current - 1);
		if (dragDepth.current === 0) setIsDraggingOver(false);
	}, []);

	const onDrop = useCallback(
		(event: DragEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();
			reset();
			if (disabled) return;
			const { files } = event.dataTransfer;
			if (files && files.length > 0) onFiles(files);
		},
		[disabled, onFiles, reset]
	);

	return { isDraggingOver, dropzoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
};

export default useFileDropzone;
